import { Service, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { API_BASE_URL } from '../api/api-config';
import { ApiEnvelope, toAuthApiError, unwrapEnvelope } from '../api/auth-envelope';
import {
  ChangePasswordErrorCode,
  CloseAllSessionsErrorCode,
  LoginErrorCode,
  LoginResult,
  LogoutErrorCode,
  ProfileErrorCode,
  ProfileResult,
  RefreshErrorCode,
  RefreshResult,
} from '../models/auth.model';

@Service()
export class AuthApiService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);

  login(email: string, password: string): Observable<LoginResult> {
    return this.http.post<ApiEnvelope<LoginResult>>(`${this.apiBaseUrl}/v1/auth/login`, { email, password }).pipe(
      unwrapEnvelope(),
      catchError((response: HttpErrorResponse) => throwError(() => toAuthApiError<LoginErrorCode>(response, 'INVALID_CREDENTIALS'))),
    );
  }

  refresh(refreshToken: string): Observable<RefreshResult> {
    return this.http.post<ApiEnvelope<RefreshResult>>(`${this.apiBaseUrl}/v1/auth/refresh`, { refreshToken }).pipe(
      unwrapEnvelope(),
      catchError((response: HttpErrorResponse) =>
        throwError(() => toAuthApiError<RefreshErrorCode>(response, 'REFRESH_TOKEN_INVALID')),
      ),
    );
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.apiBaseUrl}/v1/auth/logout`, {}).pipe(
      catchError((response: HttpErrorResponse) =>
        throwError(() => toAuthApiError<LogoutErrorCode>(response, 'AUTHENTICATION_REQUIRED')),
      ),
    );
  }

  getProfile(): Observable<ProfileResult> {
    return this.http.get<ApiEnvelope<ProfileResult>>(`${this.apiBaseUrl}/v1/auth/perfil`).pipe(
      unwrapEnvelope(),
      catchError((response: HttpErrorResponse) =>
        throwError(() => toAuthApiError<ProfileErrorCode>(response, 'AUTHENTICATION_REQUIRED')),
      ),
    );
  }

  closeAllSessions(): Observable<void> {
    return this.http.post<void>(`${this.apiBaseUrl}/v1/auth/cerrar_todas_las_sesiones`, {}).pipe(
      catchError((response: HttpErrorResponse) =>
        throwError(() => toAuthApiError<CloseAllSessionsErrorCode>(response, 'AUTHENTICATION_REQUIRED')),
      ),
    );
  }

  changePassword(currentPassword: string, newPassword: string, newPasswordConfirmation: string): Observable<void> {
    return this.http
      .put<void>(`${this.apiBaseUrl}/v1/auth/password`, { currentPassword, newPassword, newPasswordConfirmation })
      .pipe(
        catchError((response: HttpErrorResponse) =>
          throwError(() => toAuthApiError<ChangePasswordErrorCode>(response, 'VALIDATION_ERROR')),
        ),
      );
  }
}
